import { Button, Drawer, Layout } from 'antd';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import { useState } from 'react';

import { breakpoints, palette } from 'theme/variables';
import { getActiveOrganisation, getUserRole } from 'features/user/selectors/userSelectors';
import { toggleUserOrganisationSelectModal } from 'features/user/actions/userActions';
import { logOutRequest } from 'features/auth/actions/authActions';

import { MenuContent } from './MenuContent';
import { Logo } from '.';

const TopBar = styled(Layout.Header)`
    &&& {
        padding: 0 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 52px;
        background: #132b1c;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 10;

        ${breakpoints.small} {
            height: 56px;
            padding: 0 16px;
        }

        ${breakpoints.medium} {
            display: none;
        }
    }

    a {
        display: flex;
        justify-content: center;
    }
`;

const NavBtn = styled(Button)`
    &&& {
        border: none;
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.7);
        border-radius: 8px;
        width: 36px;
        height: 36px;
        min-width: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        transition: all 0.15s;

        .anticon {
            font-size: 16px;
        }

        ${breakpoints.small} {
            width: 40px;
            height: 40px;
            min-width: 40px;
            border-radius: 10px;

            .anticon {
                font-size: 18px;
            }
        }

        &:hover,
        &:focus {
            background: rgba(255, 255, 255, 0.14);
            color: #fff;
        }
    }
`;

const LogoCenter = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    min-width: 0;
    overflow: hidden;

    img {
        max-width: 80px;
        margin: 0;

        ${breakpoints.small} {
            max-width: 110px;
        }
    }
`;

const LogoutBtn = styled(Button)`
    &&& {
        border: none;
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.7);
        border-radius: 8px;
        height: 36px;
        width: 36px;
        min-width: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        transition: all 0.15s;

        .anticon {
            font-size: 16px;
        }

        ${breakpoints.small} {
            width: auto;
            height: 40px;
            border-radius: 10px;
            padding: 0 14px;
            font-family: 'Inter', sans-serif;
            font-size: 0.82rem;
            gap: 6px;
        }

        &:hover,
        &:focus {
            background: rgba(255, 255, 255, 0.14);
            color: #fff;
        }
    }
`;

const LogoutText = styled.span`
    display: none;

    ${breakpoints.small} {
        display: inline;
    }
`;

const StyledDrawer = styled(Drawer)`
    display: block;

    ${breakpoints.medium} {
        display: none;
    }

    &&& .ant-drawer-content-wrapper {
        box-shadow: 8px 0 32px rgba(0, 0, 0, 0.25);
        max-width: 85vw;
    }

    &&& .ant-drawer-header {
        background: #132b1c;
        height: 52px;
        border: none;
        padding-left: 12px;

        ${breakpoints.small} {
            height: 56px;
        }

        .ant-drawer-close {
            position: absolute;
            top: 12px;
            right: 4px;
            color: rgba(255, 255, 255, 0.4);
            font-size: 16px;
            transition: color 0.15s;

            &:hover {
                color: #fff;
            }

            ${breakpoints.small} {
                top: 16px;
            }
        }
    }

    &&& .ant-drawer-body {
        background: #132b1c;
        padding: 0;
        display: flex;
        flex-direction: column;
    }
`;

const OrgBar = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    color: ${palette.forest};
    font-size: 0.8rem;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: ${palette.warm100};
    border-bottom: 1px solid ${palette.warm200};
    transition: background 0.15s;

    &:hover {
        background: ${palette.warmBg};
        color: ${palette.fern};
    }

    ${breakpoints.medium} {
        display: none;
    }
`;

type Props = {
    isAuthenticated: boolean;
};

export const MobileMenu = ({ isAuthenticated }: Props) => {
    const dispatch = useDispatch();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const userRole = useSelector(getUserRole);
    const activeOrganisation = useSelector(getActiveOrganisation);

    const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

    return (
        <>
            <TopBar>
                {isAuthenticated && userRole && (
                    <NavBtn type="ghost" onClick={toggleDrawer} icon={<MenuOutlined />} />
                )}
                <LogoCenter>
                    <Logo />
                </LogoCenter>
                {isAuthenticated && (
                    <LogoutBtn
                        type="ghost"
                        onClick={() => dispatch(logOutRequest())}
                        icon={<LogoutOutlined />}
                    >
                        <LogoutText>Logout</LogoutText>
                    </LogoutBtn>
                )}
            </TopBar>
            {isAuthenticated && userRole && (
                <StyledDrawer
                    title={<Logo onClick={toggleDrawer} />}
                    placement="left"
                    width={280}
                    onClose={toggleDrawer}
                    visible={isDrawerOpen}
                >
                    <MenuContent onLinkClick={toggleDrawer} />
                </StyledDrawer>
            )}
            {isAuthenticated && activeOrganisation && (
                <OrgBar onClick={() => dispatch(toggleUserOrganisationSelectModal(true))}>
                    {activeOrganisation.organisation.name}
                </OrgBar>
            )}
        </>
    );
};
